import { useMemo } from "react";
import LazyApexChart from '../../../client/components/LazyApexChart';
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { chartColors, pnlColor } from "../chart/chartColors";
import ChartViewToggle from "../ChartViewToggle";
import WidgetDataTable from "../WidgetDataTable";
import type { WidgetViewProps } from "../types";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function fmtShort(v: number): string {
  return Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(1);
}

export default function MonthlyPnlWidget({ viewMode = "chart", onSetViewMode }: WidgetViewProps) {
  const { analyticsData, isDark, themeId, t } = useDashboardData();

  const tableColumns = [
    { key: "month", label: t.analytics.monthLabel, align: "left" as const },
    { key: "pnl", label: t.analytics.pnlLabel, align: "right" as const },
    { key: "trades", label: t.analytics.trades, align: "right" as const },
    { key: "winRate", label: t.analytics.winRateLabel, align: "right" as const },
    { key: "profitFactor", label: t.analytics.profitFactor, align: "right" as const },
  ];
  const tableRows = (analyticsData?.monthlyStats ?? []).map((m) => ({
    month: m.month,
    pnl: (
      <span className={m.totalPnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"}>
        {m.totalPnl >= 0 ? "+" : ""}
        {m.totalPnl.toFixed(2)}
      </span>
    ),
    trades: m.trades,
    winRate: `${m.winRate.toFixed(1)}%`,
    profitFactor: Number.isFinite(m.profitFactor) ? m.profitFactor.toFixed(2) : "—",
  }));

  const chartOptions = useMemo(() => {
    if (!analyticsData || analyticsData.monthlyStats.length === 0) return null;
    const { monthlyStats } = analyticsData;
    const c = chartColors();
    return {
      chart: {
        type: "bar" as const,
        toolbar: { show: false },
        background: "transparent",
        foreColor: c.axis,
      },
      plotOptions: {
        bar: { borderRadius: 3, columnWidth: "60%", distributed: true },
      },
      xaxis: {
        categories: monthlyStats.map((m) => m.month),
        labels: {
          style: { colors: c.axis, fontSize: "10px" },
          rotate: -45,
          rotateAlways: false,
          hideOverlappingLabels: true,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: { colors: c.axis, fontSize: "11px" },
          formatter: (v: number) => fmtShort(v) + "$",
        },
      },
      grid: {
        borderColor: c.grid,
        strokeDashArray: 4,
      },
      tooltip: {
        theme: isDark ? "dark" : "light",
        y: {
          formatter: (v: number) =>
            `${v >= 0 ? "+" : ""}${v.toFixed(2)} $`,
        },
      },
      colors: monthlyStats.map((m) => pnlColor(m.totalPnl)),
      legend: { show: false },
      dataLabels: { enabled: false },
    };
  }, [analyticsData, isDark]);

  if (!analyticsData || analyticsData.monthlyStats.length === 0) {
    return (
      <Card className="h-full">
        <CardContent className="h-full p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.dashboard.widgets.monthlyPnl}
        isDark={isDark}
        rightContent={<ChartViewToggle value={viewMode} onChange={onSetViewMode} />}
      />
      <CardContent className="px-5 pb-5 pt-1 flex-1 min-h-0">
        {viewMode === "table" ? (
          <WidgetDataTable columns={tableColumns} rows={tableRows} emptyLabel={t.analytics.noDataAvailable} />
        ) : chartOptions ? (
          <LazyApexChart
            key={`monthly-pnl-widget-${themeId}`}
            options={chartOptions}
            series={[
              {
                name: "P&L",
                data: analyticsData.monthlyStats.map(
                  (m) => Math.round(m.totalPnl * 100) / 100,
                ),
              },
            ]}
            type="bar"
            height={240}
          />
        ) : (
          <EmptyState label={t.analytics.noDataAvailable} />
        )}
      </CardContent>
    </Card>
  );
}
